import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import CallToAction, {
  ICallToActionProps,
} from '../../components/common/blocks/CallToAction';
import TrustFacts, {
  ITrustFactsProps,
} from '../../components/common/blocks/TrustFacts';
import { ITrustedByProps } from '../../components/common/blocks/TrustedBy';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { Button } from '../../components/common/ui';
import { IContactProps } from '../../components/contact-us/Contact';
import Hero, { IHeroComponentProps } from '../../components/home/Hero';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICustomerReview,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  IMedia,
  ITestimonialsPage,
  ITrustedByComponent,
  ITrustFacts,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToElement,
} from '../../shared/useCommon';
import {
  generateCustomerReviewsSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getTranslation,
  keepObjectProperties,
} from '../../shared/useUtils';
import { TestimonialsStyle } from '../../styles/pages/testimonials';
import { NextPageWithLayout } from '../_app';

export interface ITestimonialsPageProps extends ILayoutPageProps {
  hero: IHeroComponentProps;
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  callToAction: ICallToActionComponent;
  callToActionWithImage: ICallToActionComponent;
  trustedBy: ITrustedByComponent;
  reviewsTitle: string;
  reviews: ICustomerReview[];
  // customerReviewsMeta: ICustomerReviewsMeta;
  testimonialTips: { title: string; subTitle: string; icon: IMedia }[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  externalLinkButtonOne: { label: string; link: string };
  externalLinkButtonTwo: { label: string; link: string };
  trustFactsProps: ITrustFactsProps;
}

const TestimonialsPage: NextPageWithLayout<ITestimonialsPageProps> = (
  props: ITestimonialsPageProps
) => {
  const router = useRouter();

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  const trustedByProps: ITrustedByProps = {
    title: props.trustedBy.title,
    companies: props.trustedBy.logos.map((x) => ({
      company: 'company',
      image: (
        <Image
          src={x.url as string}
          fill
          sizes="175px"
          alt={x.alternativeText || ''}
        />
      ),
    })),
  };

  const callToActionProps: ICallToActionProps = {
    title: props.callToAction.title,
    buttons: props.callToAction.buttons,
  };

  const heroProps: IHeroComponentProps = {
    title: props.hero.title,
    description: props.hero.description,
    buttonLabel: props.hero.buttonLabel,
    buttonAction: () => scrollToElement('reviews'),
    secondaryButtonLabel: props.hero.secondaryButtonLabel,
    secondaryButtonAction: () => scrollToElement('contact'),
    backgroundUrl:
      props.hero.backgroundUrl || '/assets/images/home/map-image.png',
    titleHighlightedText: props.hero.titleHighlightedText,
    backgroundAlt: props.hero.backgroundAlt || 'Hero Background Image',
  };

  const callToActionWithImageComponentProps = {
    title: props.callToActionWithImage?.title || '',
    content: props.callToActionWithImage?.content || '',
    image: props.callToActionWithImage?.image!,
    buttons:
      props.callToActionWithImage?.buttons.map((button, index) => {
        if (index === 0) {
          button.action = () => scrollToElement('contact');
        } else if (index === 1) {
          button.action = () => scrollToElement('services');
        }
        return button;
      }) || [],
  };

  const ContactComponent = dynamic(
    () => import('../../components/contact-us/Contact')
  );
  const TrustedByComponent = dynamic(
    () => import('../../components/common/blocks/TrustedBy')
  );
  const CallToActionWithImageComponent = dynamic(
    () => import('../../components/common/blocks/CallToActionWithImage')
  );

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <meta
          name="facebook-domain-verification"
          content="v1rp4jxjfkqwderk1ntxo8bjfy51hz"
        />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <Hero {...heroProps} />
        </div>
        <div className="page-content-block">
          <TrustFacts {...props.trustFactsProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <div className={TestimonialsStyle['testimonials-tips']}>
            {props.testimonialTips &&
              props.testimonialTips.map((item, index) => (
                <div
                  key={index}
                  className={TestimonialsStyle['testimonials-tips-item']}>
                  <Image
                    src={
                      item.icon?.formats?.thumbnail?.url ||
                      item.icon?.url ||
                      '/assets/images/icons/CheckmarkIcon.svg'
                    }
                    width={95}
                    height={95}
                    alt={item.icon?.alternativeText || ''}
                  />
                  <span>{item.title}</span>
                  <p>{item.subTitle}</p>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="page-content-section is-narrow">
        <div className="page-content-block is-text-align-center" id="reviews">
          <h2>{props.reviewsTitle}</h2>
          <div className={TestimonialsStyle['testimonials-reviews']}>
            {props.reviews && props.reviews.length > 0
              ? props.reviews.map((review, index) => (
                  <div
                    key={index}
                    className={`${
                      TestimonialsStyle['testimonials-reviews-item']
                    } ${
                      review.link
                        ? TestimonialsStyle[
                            'testimonials-reviews-item-clickable'
                          ]
                        : ''
                    }`}
                    onClick={() => {
                      if (review.link) {
                        window.open(review.link, '_blank');
                      }
                    }}>
                    <div
                      className={
                        TestimonialsStyle['testimonials-reviews-item-heading']
                      }>
                      <div
                        className={
                          TestimonialsStyle[
                            'testimonials-reviews-item-stars-rating'
                          ]
                        }>
                        {review?.score
                          ? [...Array(5)].map((item: any, index: number) => {
                              return (
                                <Image
                                  src="/assets/images/icons/StarIcon.svg"
                                  alt="star"
                                  width={20}
                                  height={20}
                                  priority={false}
                                  key={index}
                                  className={
                                    review.score - index > 0
                                      ? TestimonialsStyle['star-active']
                                      : undefined
                                  }
                                />
                              );
                            })
                          : ' '}
                      </div>
                      {review.link && (
                        <Image
                          className={
                            TestimonialsStyle[
                              'testimonials-reviews-item-heading-arrow'
                            ]
                          }
                          src="/assets/images/icons/ArrowRightIcon.svg"
                          alt="star"
                          width={20}
                          height={20}
                          priority={false}
                        />
                      )}
                    </div>
                    <p>
                      <strong>{review.customerName}</strong>
                    </p>
                    <p>{review.content}</p>
                  </div>
                ))
              : null}
          </div>
          <div className={TestimonialsStyle['testimonials-reviews-buttons']}>
            <Button
              color="cta"
              onClick={() => {
                window.open(props.externalLinkButtonOne.link, '_blank');
              }}>
              {props.externalLinkButtonOne.label}
            </Button>
            <Button
              color="cta-outline"
              onClick={() =>
                window.open(props.externalLinkButtonTwo.link, '_blank')
              }>
              {props.externalLinkButtonTwo.label}
            </Button>
          </div>
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <TrustedByComponent {...trustedByProps} />
        </div>
      </div>

      <div className="page-content-section is-narrow">
        <div className="page-content-block">
          <CallToActionWithImageComponent
            {...callToActionWithImageComponentProps}
          />
        </div>
      </div>

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToAction {...callToActionProps} />
        </div>
      </div>
    </div>
  );
};

TestimonialsPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: ITestimonialsPageProps }> {
  const appRoutes = await getRoutes();

  const pageUrlParams = {
    'populate[0]': 'metas',
    'populate[1]': 'hero',
    'populate[2]': 'hero.background',
    'populate[11]': 'contactUsForm',
    'populate[12]': 'contactUsFormSuccessMessage',
    'populate[13]': 'contactUsFormErrorMessage',
    'populate[14]': 'callToAction',
    'populate[15]': 'callToAction.buttons',
    'populate[16]': 'callToAction.image',
    'populate[17]': 'trustedBy',
    'populate[18]': 'trustedBy.logos',
    'populate[19]': 'customerReviews',
    'populate[20]': 'testimonialTips',
    'populate[21]': 'testimonialTips.title',
    'populate[22]': 'testimonialTips.subTitle',
    'populate[23]': 'testimonialTips.icon',
    'populate[24]': 'customerReviewsTitle',
    'populate[25]': 'externalLinkButtonOne',
    'populate[26]': 'externalLinkButtonTwo',
    'populate[27]': 'reviews',
    'populate[28]': 'callToActionWithImage',
    'populate[29]': 'callToActionWithImage.buttons',
    'populate[30]': 'callToActionWithImage.image',
    'populate[31]': 'breadcrumbAncestors',
  };

  // Get page data
  const page = (
    await fetchStrapi<any>(`/testimonials-page`, pageUrlParams, buildApiKey)
  ).data.attributes as ITestimonialsPage;

  if (!!page.hero && page.hero.length > 0) {
    page.hero.forEach((x) => {
      if (!!(x.background as any).data) {
        x.background = flatStrapiObject(x.background);
      }
    });
  }

  const customerReviewsUrlParams = {
    locale: context.locale,
  };

  const reviews = flatStrapiCollection(
    await fetchStrapi<any>(
      `/customer-reviews`,
      customerReviewsUrlParams,
      buildApiKey
    )
  ) as ICustomerReview[];
  const trustedBy: ITrustedByComponent = getTranslation(
    page.trustedBy,
    context.locale
  );
  trustedBy.logos = flatStrapiCollection(trustedBy.logos as any).map((x) =>
    keepObjectProperties<IMedia>(x, ['url', 'alternativeText', 'name'])
  );

  const metas = getTranslation(page.metas, context.locale);
  const commonTranslations = await getCommonTranslations();
  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);

  // Prepare Hero
  const heroData = getTranslation(page.hero, context.locale);
  const heroProps: IHeroComponentProps = {
    title: heroData.title,
    description: heroData.description,
    buttonLabel: heroData.buttonLabel,
    secondaryButtonLabel: heroData.secondaryButtonLabel,
    backgroundUrl:
      getImageSrcWithFallback(heroData.background, 'full') ||
      '/assets/images/home/map-image.png',
    titleHighlightedText: heroData.highlightedTitleText,
    backgroundAlt:
      flatStrapiObject<any>(heroData.background).alternativeText ||
      'Hero Background Image',
  };

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const customerReviewsSchema = generateCustomerReviewsSchema(
    reviews,
    customerReviewsMeta
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema, ...customerReviewsSchema],
  };

  // Populate breadcrumbs
  if (page.breadcrumbAncestors && page.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = page.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = heroProps.title;

  const alternativeLinks = getAlternateLinks('/testimonials', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', metas.slug);

  const callToAction = getTranslation(page.callToAction, context.locale);

  if (callToAction.image && (callToAction.image as any).data) {
    callToAction.image = flatStrapiObject(callToAction.image) as IMedia;
  }

  const callToActionWithImage = getTranslation(
    page.callToActionWithImage,
    context.locale
  );

  if (
    callToActionWithImage.image &&
    (callToActionWithImage.image as any).data
  ) {
    callToActionWithImage.image = flatStrapiObject(callToActionWithImage.image);
  }

  const trustFacts: ITrustFacts = getTranslation(
    commonTranslations.trustFactsStrip,
    context.locale
  );
  const trustFactsProps: ITrustFactsProps = {
    locale: context.locale,
    facts: (trustFacts?.facts || []).map((x) => ({
      value: x.value,
      description: x.description,
    })),
  };

  const externalLinkButtonOneTranslated = getTranslation(
    page.externalLinkButtonOne,
    context.locale
  );

  const externalLinkButtonTwoTranslated = getTranslation(
    page.externalLinkButtonTwo,
    context.locale
  );

  const externalLinkButtonOne = {
    label: externalLinkButtonOneTranslated.label,
    link: externalLinkButtonOneTranslated.link,
  };

  const externalLinkButtonTwo = {
    label: externalLinkButtonTwoTranslated.label,
    link: externalLinkButtonTwoTranslated.link,
  };

  const props: ITestimonialsPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta: metas,
    hero: heroProps,
    trustedBy,
    contactMethods,
    reviews,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormSuccessMessage: getTranslation(
      page.contactUsFormSuccessMessage,
      context.locale
    ).text as string,
    contactUsFormErrorMessage: getTranslation(
      page.contactUsFormErrorMessage,
      context.locale
    ).text as string,
    callToAction,
    callToActionWithImage,
    // customerReviewsMeta,
    alternativeLinks,
    canonicalLink,
    reviewsTitle: getTranslation(page.customerReviewsTitle, context.locale)
      .text as string,
    externalLinkButtonOne,
    externalLinkButtonTwo,
    testimonialTips: (page.testimonialTips || []).map((x) => ({
      title: getTranslation(x.title, context.locale)!.text as string,
      subTitle: getTranslation(x.subTitle, context.locale)!.text as string,
      icon: flatStrapiObject(x.icon) as IMedia,
    })),
    trustFactsProps,
  };

  return {
    props,
  };
}

export default TestimonialsPage;
